import { RcTrans } from '@/components/atoms/RcTrans'
import { ActionMenu } from '@/components/molecules/interactive/ActionMenu'
import { RcButton } from '@/components/molecules/interactive/RcButton'
import { ChatBubble } from '@/components/molecules/text/ChatBubble'
import { EllipsisBox } from '@/components/molecules/text/EllipsisBox'
import { UserAvatar } from '@/components/organisms/avatar/UserAvatar'
import { DiscoverCard } from '@/components/organisms/card/DiscoverCard'
import { usePage } from '@/components/providers/site/PageProvider'
import { expandById, ExpansionType } from '@/core/expand'
import { useApiError } from '@/core/hooks/useApiError'
import { useCompetitionTeamInvitations } from '@/entity/competition-team/useCompetitionTeamInvitations'
import { useCompetitionParticipant } from '@/entity/competition/useCompetitionParticipant'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { CompetitionParticipantDocument } from '@rallycry/api-suite-typescript/dist/models/CompetitionParticipantDocument'
import { some } from 'lodash-es'

export interface FreeAgentCardProps {
  participantDisplay: CompetitionParticipantDocument
}

export const FreeAgentCard = ({ participantDisplay }: FreeAgentCardProps) => {
  const { involvement, hasTeam } = useCompetitionParticipant()
  const { invite } = useCompetitionTeamInvitations({})
  const { handle } = useApiError()
  const { open } = usePage()

  const participantDisplayId = participantDisplay?.participant?.id
  const ledTeams = involvement?.filter(it => it?.management)
  const options =
    ledTeams?.map(it => ({
      key: it?.entry?.id!,
      display: (
        <RcTrans
          i18nKey='competition:team.invite-to'
          tOptions={{
            team: expandById(
              it?.entry?.id!,
              it._expanded,
              ExpansionType.CompetitionEntry
            )?.alternateName
          }}
        />
      ),
      action: async () => {
        await handleInvite(it?.entry?.id!)
        open(null, <RcTrans i18nKey='competition:team.invite-sent' />, 3000)
      }
    })) || []

  const handleInvite = async (teamId: number) => {
    if (!participantDisplayId) return
    try {
      await invite(participantDisplayId, teamId)
    } catch (error) {
      await handle(error)
    }
  }

  return (
    <DiscoverCard
      header={
        <Box p={3} pb={0}>
          <UserAvatar user={participantDisplay.participant!} />

          <ChatBubble
            message={
              <Tooltip title={participantDisplay.description}>
                <Box
                  minHeight={30}
                  sx={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <EllipsisBox lineClamp={2}>
                    <Typography variant='body2'>
                      {participantDisplay.description}
                    </Typography>
                  </EllipsisBox>
                </Box>
              </Tooltip>
            }
          />
        </Box>
      }
      actions={
        some(ledTeams) ? (
          <ActionMenu
            options={options}
            buttonProps={{ fullWidth: true, sx: {}, variant: 'contained' }}
          >
            <RcTrans i18nKey='shared.invite' />
          </ActionMenu>
        ) : hasTeam() ? (
          <Tooltip
            title={
              <RcTrans i18nKey='competition:team.only-leader-can-invite' />
            }
          >
            <Box width='100%'>
              <RcButton variant='outlined' disabled fullWidth>
                <RcTrans i18nKey='shared.invite' />
              </RcButton>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={<RcTrans i18nKey='competition:team.create-a-team' />}>
            <Box width='100%'>
              <RcButton variant='outlined' disabled fullWidth>
                <RcTrans i18nKey='shared.invite' />
              </RcButton>
            </Box>
          </Tooltip>
        )
      }
    />
  )
}
